import { useEffect, useId, useState } from 'react'
import { Modal, Button, Textarea } from '../ui'
import { templatesApiService } from '../../services/templatesApiService'
import { foldersApiService, type FolderPromotionApproval } from '../../services/foldersApiService'
import { ApiError } from '../../services/BaseApiService'
import { settingsApiService, type DeactivateImpact, type DualControlFallback, type TeamPromotionApproval } from '../../services/settingsApiService'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import type { ApprovalLevel, Folder, OverridesBelow, PendingPromotionRequest, PromotionApprovalRule } from '../../types'
import { colors, fontSize, radius, shadow, spacing } from '../../styles/theme'

const box: React.CSSProperties = {
  background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.sm, padding: spacing.md,
}
const radioRow: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: spacing.xs, fontSize: fontSize.sm, color: colors.text,
}
const errorBox: React.CSSProperties = {
  background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`,
  borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm,
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export const APPROVAL_LEVEL_NAME: Record<ApprovalLevel, string> = {
  auto: 'Automatic',
  self: 'Self-approval',
  dual: 'Dual control',
}

const APPROVAL_LEVEL_DESCRIPTION: Record<ApprovalLevel, string> = {
  auto: 'Promotes immediately; recorded as automatically approved.',
  self: 'You approve your own request from the email link or Approvals page; the approval is recorded.',
  dual: 'Someone else with Manage on the folder must approve (segregation of duties).',
}

const DUAL_UNAVAILABLE_DEFAULT =
  'Dual control needs a second active editor or admin to approve, and this team has only one. Invite a teammate or give one the Editor role to use it.'

/** "Dual control (from folder 'Acme')". `selfFolderId` names the folder being viewed as "this folder". */
export function approvalRuleLabel(
  rule: Pick<PromotionApprovalRule, 'level' | 'source' | 'sourceFolderId'>,
  folders: Folder[] = [],
  selfFolderId?: string,
): string {
  const value = APPROVAL_LEVEL_NAME[rule.level]
  switch (rule.source) {
    case 'template': return `${value} (this template)`
    case 'folder': {
      if (rule.sourceFolderId && rule.sourceFolderId === selfFolderId) return `${value} (this folder)`
      const name = folders.find(f => f.folderId === rule.sourceFolderId)?.name
      return `${value} (from folder ${name ? `'${name}'` : 'above'})`
    }
    case 'team': return `${value} (team default)`
    default: return `${value} (default — nothing set)`
  }
}

/** Ask about clearing overrides below only for an explicit value and when there are some. */
export function shouldAskApplyBelow(setting: ApprovalLevel | null, below: OverridesBelow | null | undefined): boolean {
  return setting !== null && !!below && below.folders + below.templates > 0
}

/** Inherit (null) or one of the three levels, each with a one-line description. Dual is disabled when unavailable. */
export function ApprovalLevelSelector({ name, value, onChange, disabled, inheritLabel = 'Inherit', dualAvailable = true, dualUnavailableReason }: {
  name: string
  value: ApprovalLevel | null
  onChange: (value: ApprovalLevel | null) => void
  disabled?: boolean
  inheritLabel?: string
  dualAvailable?: boolean
  dualUnavailableReason?: string | null
}) {
  const id = useId()
  const options: { v: ApprovalLevel | null; label: string; description?: string }[] = [
    { v: null, label: inheritLabel },
    ...(['auto', 'self', 'dual'] as ApprovalLevel[]).map(v => ({ v, label: APPROVAL_LEVEL_NAME[v], description: APPROVAL_LEVEL_DESCRIPTION[v] })),
  ]
  return (
    <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
      {options.map(o => {
        const key = o.v ?? 'inherit'
        const off = disabled || (o.v === 'dual' && !dualAvailable)
        return (
          <div key={key} style={{ ...radioRow, opacity: off ? 0.6 : 1 }}>
            <input
              type="radio" id={`${id}-${key}`} name={name} checked={value === o.v} disabled={off}
              aria-describedby={o.description ? `${id}-${key}-desc` : undefined}
              onChange={() => onChange(o.v)} style={{ marginTop: 3 }}
            />
            <div>
              <label htmlFor={`${id}-${key}`} style={{ cursor: off ? 'default' : 'pointer' }}>{o.label}</label>
              {o.description && (
                <div id={`${id}-${key}-desc`} style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>{o.description}</div>
              )}
              {o.v === 'dual' && !dualAvailable && (
                <div data-testid="dual-unavailable" style={{ fontSize: fontSize.xs, color: colors.warning, marginTop: 2 }}>
                  {dualUnavailableReason || DUAL_UNAVAILABLE_DEFAULT}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function ApplyBelowModal({ below, loading, onApply, onThisLevel, onCancel }: {
  below: OverridesBelow
  loading?: boolean
  onApply: () => void
  onThisLevel: () => void
  onCancel: () => void
}) {
  return (
    <Modal
      open
      onClose={onCancel}
      title="Apply to everything below?"
      width={460}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={loading}>Cancel</Button>
          <Button variant="secondary" onClick={onThisLevel} disabled={loading}>Only this level</Button>
          <Button variant="primary" onClick={onApply} loading={loading}>Apply to everything below</Button>
        </>
      }
    >
      <div style={{ fontSize: fontSize.base, color: colors.textSecondary, lineHeight: 1.5 }}>
        {plural(below.folders, 'folder')} and {plural(below.templates, 'template')} below have their own setting.
        Applying clears those so they follow this setting.
      </div>
    </Modal>
  )
}

export function DecisionNoteModal({ title, confirmLabel, danger, loading, error, onConfirm, onCancel }: {
  title: string
  confirmLabel: string
  danger?: boolean
  loading?: boolean
  error?: string
  onConfirm: (note: string) => void
  onCancel: () => void
}) {
  const [note, setNote] = useState('')
  return (
    <Modal
      open
      onClose={onCancel}
      title={title}
      width={440}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={loading}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={() => onConfirm(note.trim())} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        {error && <div role="alert" style={errorBox}>{error}</div>}
        <Textarea aria-label="Note" placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} rows={3} />
      </div>
    </Modal>
  )
}

export function decisionErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 403) return 'You cannot decide this request. Approvers need Manage on the folder, and under Dual control cannot approve their own request.'
  if (err instanceof ApiError && err.status === 409) return 'This request was already decided, or its version no longer exists.'
  return err instanceof ApiError ? err.message : fallback
}

type Decision = 'approve' | 'reject' | 'cancel'

/**
 * Runs approve / reject / cancel on one request, owning the note modal and its errors.
 * `onDone` gets a message for the page's notice.
 */
export function useRequestDecision(onDone: (message: string) => void) {
  const [target, setTarget] = useState<{ decision: Decision; request: { promotionRequestId: string; versionNumber: number } } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function run(note: string) {
    if (!target) return
    const { decision, request } = target
    setLoading(true); setError('')
    try {
      let message: string
      if (decision === 'approve') {
        const r = await templatesApiService.approvePromotionRequest(request.promotionRequestId, note)
        message = r.scheduled && r.promoteAt
          ? `Approved. v${r.versionNumber} is scheduled to go live at ${new Date(r.promoteAt).toLocaleString()}.`
          : `Approved. v${r.versionNumber} is now live.`
      } else if (decision === 'reject') {
        await templatesApiService.rejectPromotionRequest(request.promotionRequestId, note)
        message = `Request for v${request.versionNumber} rejected.`
      } else {
        await templatesApiService.cancelPromotionRequest(request.promotionRequestId, note)
        message = `Request for v${request.versionNumber} cancelled.`
      }
      setTarget(null)
      onDone(message)
    } catch (err) {
      setError(decisionErrorMessage(err, 'Action failed'))
    } finally {
      setLoading(false)
    }
  }

  const open = (decision: Decision, request: { promotionRequestId: string; versionNumber: number }) => {
    setError(''); setTarget({ decision, request })
  }

  const modal = target && (
    <DecisionNoteModal
      title={
        target.decision === 'approve' ? `Approve v${target.request.versionNumber} for Live?`
          : target.decision === 'reject' ? `Reject the request for v${target.request.versionNumber}?`
            : `Cancel the request for v${target.request.versionNumber}?`
      }
      confirmLabel={target.decision === 'approve' ? 'Approve' : target.decision === 'reject' ? 'Reject' : 'Cancel request'}
      danger={target.decision !== 'approve'}
      loading={loading}
      error={error}
      onConfirm={run}
      onCancel={() => setTarget(null)}
    />
  )

  return { open, modal }
}

export function PendingPromotionRequestBanner({ request, onChanged }: {
  request: PendingPromotionRequest
  onChanged: (message: string) => void
}) {
  const { open, modal } = useRequestDecision(onChanged)
  const me = useCurrentUser()
  const own = !!me && request.requestedBy === me.userId
  return (
    <div data-testid="pending-approval" style={{
      background: colors.warningLight, border: `1px solid ${colors.warningBorder}`, borderRadius: radius.sm,
      padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, color: colors.text, marginBottom: spacing.md,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap',
    }}>
      <div>
        <div>
          <strong>v{request.versionNumber}</strong> is awaiting approval — {APPROVAL_LEVEL_NAME[request.approvalLevel]} (requested by {request.requestedByEmail ?? 'a teammate'},{' '}
          {new Date(request.requestedAt).toLocaleString()}
          {request.promoteAt && <>, scheduled for {new Date(request.promoteAt).toLocaleString()}</>})
        </div>
        {request.changeNote && <div style={{ color: colors.textSecondary, marginTop: 2 }}>{request.changeNote}</div>}
      </div>
      <div style={{ display: 'flex', gap: spacing.sm }}>
        {request.canApprove && <Button size="sm" onClick={() => open('approve', request)}>Approve</Button>}
        {request.canApprove && !own && <Button size="sm" variant="danger" onClick={() => open('reject', request)}>Reject</Button>}
        {request.canCancel && <Button size="sm" variant="ghost" onClick={() => open('cancel', request)}>Cancel request</Button>}
      </div>
      {modal}
    </div>
  )
}

/** Effective rule for a template, with an override for team admins. */
export function TemplatePromotionApprovalSetting({ templateId, rule, folders, isAdmin, onSaved }: {
  templateId: string
  rule: PromotionApprovalRule
  folders: Folder[]
  isAdmin: boolean
  onSaved: (rule: PromotionApprovalRule) => void
}) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function change(setting: ApprovalLevel | null) {
    setSaving(true); setError('')
    try { onSaved({ ...rule, ...(await templatesApiService.setPromotionApproval(templateId, setting)) }) }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Failed to save') }
    finally { setSaving(false) }
  }

  return (
    <div style={box} data-testid="promotion-approval-setting">
      <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text }}>
        Promotion approval: {approvalRuleLabel(rule, folders)}
      </div>
      <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs, marginBottom: isAdmin ? spacing.sm : 0 }}>
        {APPROVAL_LEVEL_DESCRIPTION[rule.level]}
      </div>
      {isAdmin && (
        <ApprovalLevelSelector name={`template-approval-${templateId}`} value={rule.setting} onChange={change} disabled={saving}
          dualAvailable={rule.dualAvailable} dualUnavailableReason={rule.dualUnavailableReason} />
      )}
      {error && <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm, marginTop: spacing.xs }}>{error}</div>}
    </div>
  )
}

/** Team admins: a folder's override, applied to the subtree or this level only. */
export function FolderPromotionApprovalModal({ folder, folders, onClose, onSaved }: {
  folder: Folder
  folders: Folder[]
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const [state, setState] = useState<FolderPromotionApproval | null>(null)
  const [setting, setSetting] = useState<ApprovalLevel | null>(null)
  const [asking, setAsking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    foldersApiService.getPromotionApproval(folder.folderId)
      .then(s => { setState(s); setSetting(s.setting) })
      .catch(err => setError(err instanceof ApiError ? err.message : 'Failed to load'))
  }, [folder.folderId])

  async function save(applyToDescendants: boolean) {
    setSaving(true); setError('')
    try {
      const r = await foldersApiService.setPromotionApproval(folder.folderId, setting, applyToDescendants)
      const cleared = r.cleared.folders + r.cleared.templates
      onSaved(cleared > 0
        ? `Promotion approval saved for "${folder.name}". Cleared ${plural(r.cleared.folders, 'folder')} and ${plural(r.cleared.templates, 'template')} below.`
        : `Promotion approval saved for "${folder.name}".`)
      onClose()
    } catch (err) {
      setAsking(false)
      setError(err instanceof ApiError ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  function handleSave() {
    if (shouldAskApplyBelow(setting, state?.overridesBelow)) setAsking(true)
    else save(false)
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Promotion approval"
        subtitle={`Folder "${folder.name}"`}
        width={460}
        footer={
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving && !asking} disabled={!state}>Save</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {error && <div role="alert" style={errorBox}>{error}</div>}
          {!state ? (
            !error && <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading...</div>
          ) : (
            <>
              <div style={{ fontSize: fontSize.sm, color: colors.text }}>
                Currently: <strong>{approvalRuleLabel(state, folders, folder.folderId)}</strong>
              </div>
              <div style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                Inherit follows the nearest parent folder with a setting, then the team.
              </div>
              <ApprovalLevelSelector name="folder-approval" value={setting} onChange={setSetting} disabled={saving}
                dualAvailable={state.dualAvailable} dualUnavailableReason={state.dualUnavailableReason} />
            </>
          )}
        </div>
      </Modal>
      {asking && state && (
        <ApplyBelowModal
          below={state.overridesBelow}
          loading={saving}
          onApply={() => save(true)}
          onThisLevel={() => save(false)}
          onCancel={() => setAsking(false)}
        />
      )}
    </>
  )
}

/** Team admins: the team-wide setting, the last level before the automatic default. */
export function TeamPromotionApprovalSetting() {
  const [state, setState] = useState<TeamPromotionApproval | null>(null)
  const [asking, setAsking] = useState<ApprovalLevel | null | undefined>(undefined)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = () => settingsApiService.getTeamPromotionApproval().then(setState).catch(() => setState(null))
  useEffect(() => { load() }, [])
  if (!state) return null

  async function save(setting: ApprovalLevel | null, applyToDescendants: boolean) {
    setSaving(true); setError(''); setNotice('')
    try {
      const r = await settingsApiService.setTeamPromotionApproval(setting, applyToDescendants)
      const cleared = r.cleared.folders + r.cleared.templates
      if (cleared > 0) setNotice(`Cleared ${plural(r.cleared.folders, 'folder')} and ${plural(r.cleared.templates, 'template')} with their own setting.`)
      setAsking(undefined)
      await load()
    } catch (err) {
      setAsking(undefined)
      setError(err instanceof ApiError ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  function change(setting: ApprovalLevel | null) {
    if (shouldAskApplyBelow(setting, state?.overridesBelow)) setAsking(setting)
    else save(setting, false)
  }

  return (
    <div style={{ ...box, marginBottom: spacing.xl }} data-testid="team-promotion-approval">
      <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
        Approval to promote to Live
      </div>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.sm }}>
        Team-wide setting. Folders and templates can override it.
      </div>
      <ApprovalLevelSelector name="team-approval" value={state.setting} onChange={change} disabled={saving} inheritLabel="Not set (Automatic)"
        dualAvailable={state.dualAvailable} dualUnavailableReason={state.dualUnavailableReason} />
      {notice && <div style={{ color: colors.success, fontSize: fontSize.sm, marginTop: spacing.xs }}>{notice}</div>}
      {error && <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm, marginTop: spacing.xs }}>{error}</div>}
      {asking !== undefined && (
        <ApplyBelowModal
          below={state.overridesBelow}
          loading={saving}
          onApply={() => save(asking, true)}
          onThisLevel={() => save(asking, false)}
          onCancel={() => setAsking(undefined)}
        />
      )}
    </div>
  )
}

const approvers = (n: number) => (n === 1 ? 'one editor or admin' : `${n} editors or admins`)

/**
 * Deactivating this member (or, with `newRoleLabel`, lowering their role) leaves dual control
 * unsatisfiable; the admin picks what replaces it. Confirm passes the chosen fallback (Automatic by default).
 */
export function DualControlFallbackModal({ email, impact, newRoleLabel, loading, error, onConfirm, onCancel }: {
  email: string
  impact: DeactivateImpact
  newRoleLabel?: string
  loading?: boolean
  error?: string
  onConfirm: (fallback: DualControlFallback) => void
  onCancel: () => void
}) {
  const [fallback, setFallback] = useState<DualControlFallback>('auto')
  const d = impact.dualControl
  const uses = [
    ...(d.team ? ['the team setting'] : []),
    ...(d.folders > 0 ? [plural(d.folders, 'folder')] : []),
    ...(d.templates > 0 ? [plural(d.templates, 'template')] : []),
    ...(d.pendingRequests > 0 ? [plural(d.pendingRequests, 'pending request')] : []),
  ]
  return (
    <Modal
      open
      onClose={onCancel}
      title="Replace dual control?"
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={loading}>Cancel</Button>
          <Button variant="danger" onClick={() => onConfirm(fallback)} loading={loading}>{newRoleLabel ? 'Change role' : 'Deactivate'}</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, fontSize: fontSize.sm, color: colors.text, lineHeight: 1.5 }}>
        {error && <div role="alert" style={errorBox}>{error}</div>}
        <div>
          {newRoleLabel
            ? <>Changing <strong>{email}</strong> to {newRoleLabel} leaves {approvers(impact.activeAfter)} who could approve,</>
            : <>Deactivating <strong>{email}</strong> leaves {approvers(impact.activeAfter)} who could approve,</>}
          {' '}so dual control can no longer be satisfied.
          {uses.length > 0 && <> It is used by: {uses.join(', ')}.</>}
        </div>
        <div role="radiogroup" aria-label="Replace dual control with" style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <div style={{ fontWeight: 600 }}>Replace dual control with:</div>
          {(['auto', 'self'] as DualControlFallback[]).map(v => (
            <label key={v} style={{ ...radioRow, cursor: 'pointer' }}>
              <input type="radio" name="dual-fallback" checked={fallback === v} disabled={loading} onChange={() => setFallback(v)} />
              {APPROVAL_LEVEL_NAME[v]}
            </label>
          ))}
        </div>
        <div style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
          Pending dual-control requests become self-approval requests.
        </div>
      </div>
    </Modal>
  )
}
